import { contactEmailLink, contactLinks, contactPageLink, formatLegalDate, type LegalApp, operatorWithDetails } from "./app";
import type { LegalBlock, LegalDocument, LegalProcessor, LegalSection, LegalText } from "./types";

type DocumentFrame = {
  title?: string;
  lead?: string;
  notice?: LegalText;
  summary?: LegalText;
  contents?: boolean;
  numbered?: boolean;
  closing?: readonly LegalText[];
};

export type PrivacyProcessors = { intro?: LegalText; list: readonly LegalProcessor[]; notes?: readonly LegalBlock[] };

export type PrivacyDetails = DocumentFrame & {
  responsible?: readonly LegalBlock[];
  collected: readonly LegalBlock[];
  sections?: readonly LegalSection[];
  payments?: readonly LegalBlock[];
  processors: PrivacyProcessors;
  handling?: readonly LegalSection[];
  retention: readonly LegalBlock[];
  rights?: readonly LegalBlock[];
  more?: readonly LegalSection[];
  changes: LegalText;
};

export type TermsDetails = DocumentFrame & {
  agreement?: readonly LegalBlock[];
  sections: readonly LegalSection[];
  changes: LegalText;
};

export type LegalPageDetails = DocumentFrame & { title: string; sections: readonly LegalSection[] };

const section = (id: string, heading: string, blocks: readonly LegalBlock[]): LegalSection => ({ id, heading, blocks });

const frameOf = (app: LegalApp, frame: DocumentFrame, title: string, sections: readonly LegalSection[]): LegalDocument => ({
  title: frame.title ?? title,
  updated: formatLegalDate(app.effectiveDate),
  sections,
  ...(frame.lead === undefined ? {} : { lead: frame.lead }),
  ...(frame.notice === undefined ? {} : { notice: frame.notice }),
  ...(frame.summary === undefined ? {} : { summary: frame.summary }),
  ...(frame.contents === undefined ? {} : { contents: frame.contents }),
  ...(frame.numbered === undefined ? {} : { numbered: frame.numbered }),
  ...(frame.closing === undefined ? {} : { closing: frame.closing }),
});

const processorLines = ({ list }: PrivacyProcessors): LegalBlock => ({ list: list.map(({ name, purpose }) => `**${name}.** ${purpose}`) });

const privacyIdentity = (app: LegalApp, details: PrivacyDetails): LegalSection =>
  section("who-we-are", "Who we are", [
    `${app.product} is operated by ${operatorWithDetails(app)} ("we", "us"), which is responsible for the personal data described here. You can reach us at ${contactLinks(app)}.`,
    ...(details.responsible ?? []),
  ]);

const privacyProcessors = ({ processors }: PrivacyDetails): LegalSection =>
  section("processors", "Who processes data for us", [
    ...(processors.intro ? [processors.intro] : []),
    processorLines(processors),
    ...(processors.notes ?? []),
  ]);

const optionalSection = (id: string, heading: string, blocks: readonly LegalBlock[] | undefined): LegalSection[] =>
  blocks === undefined ? [] : [section(id, heading, blocks)];

export const privacyPolicy = (app: LegalApp, details: PrivacyDetails): LegalDocument =>
  frameOf(app, details, "Privacy policy", [
    privacyIdentity(app, details),
    section("what-we-collect", "What we collect", details.collected),
    ...(details.sections ?? []),
    ...optionalSection("payments", "Payments", details.payments),
    privacyProcessors(details),
    ...(details.handling ?? []),
    section("retention", "How long we keep it", details.retention),
    ...optionalSection("your-rights", "Your rights", details.rights),
    ...(details.more ?? []),
    section("changes", "Changes to this policy", [details.changes]),
    section("contact", "Contact", [
      `For questions about this policy, or any request about your data, email ${contactEmailLink(app)} or use our ${contactPageLink(app)}.`,
    ]),
  ]);

const termsIdentity = (app: LegalApp, details: TermsDetails): LegalSection =>
  section("who-we-are", "Who we are", [
    `${app.product} (${app.site}) is operated by ${operatorWithDetails(app)} ("we", "us"). These terms are the agreement between you and ${app.operator} whenever you use ${app.product}.`,
    ...(details.agreement ?? []),
  ]);

export const termsOfUse = (app: LegalApp, details: TermsDetails): LegalDocument =>
  frameOf(app, details, "Terms of use", [
    termsIdentity(app, details),
    ...details.sections,
    section("changes", "Changes to these terms", [details.changes]),
    section("contact", "Contact", [`For questions about these terms, email ${contactEmailLink(app)} or use our ${contactPageLink(app)}.`]),
  ]);

export const legalPage = (app: LegalApp, details: LegalPageDetails): LegalDocument => frameOf(app, details, details.title, details.sections);
